import { diaryCategoryLabelOf, type DiaryEntry, type DiaryPrompt } from "@couples/shared";
import { Bookmark, Pencil, Trash2 } from "lucide-react";

import { withAlpha } from "../lib/color";
import { formatDateTimeJa } from "../lib/dates";

interface Props {
  entry: DiaryEntry;
  authorName: string;
  /** 差出人ラベルの色。プロフィールで設定した「好きな色」(→ lib/profileItems.ts) */
  authorColor: string;
  prompt: DiaryPrompt | undefined;
  isBookmarked: boolean;
  onToggleBookmark: () => void;
  onEdit: (() => void) | undefined;
  onDelete: (() => void) | undefined;
  /** 添付した画像のID(交換日記タグ付きでアルバムにも入っている) */
  mediaIds: string[];
  thumbUrlOf: (mediaId: string) => string | undefined;
  onOpenMedia: (mediaId: string) => void;
}

export default function DiaryCard({
  entry,
  authorName,
  authorColor,
  prompt,
  isBookmarked,
  onToggleBookmark,
  onEdit,
  onDelete,
  mediaIds,
  thumbUrlOf,
  onOpenMedia,
}: Props) {
  return (
    <div className="relative rounded-[3px] bg-white p-4 pt-3.5 shadow-sm">
      {/* ノートの綴じ目風。本文の罫線と対にするため見出し行の外に置く */}
      <span aria-hidden className="diary-page-fold" />

      <div className="flex items-start justify-between gap-2">
        {/* 差出人=誰のページかを一目で示す小さなラベル。色はその人がプロフィールで選んだ「好きな色」 */}
        <span
          className="inline-block rounded-sm px-2 py-0.5 text-[11px] font-semibold"
          style={{ backgroundColor: withAlpha(authorColor, "1a"), color: authorColor }}
        >
          {authorName}
        </span>
        <div className="flex items-center gap-2">
          <span className="diary-stamp">{formatDateTimeJa(entry.createdAt)}</span>
          <button
            type="button"
            onClick={onToggleBookmark}
            aria-label={isBookmarked ? "しおりを外す" : "しおりを挟む"}
            aria-pressed={isBookmarked}
            className={isBookmarked ? "text-[var(--color-accent)]" : "text-[var(--color-sub)]"}
          >
            <Bookmark
              className="size-3.5"
              aria-hidden
              fill={isBookmarked ? "currentColor" : "none"}
            />
          </button>
          {onEdit !== undefined && (
            <button
              type="button"
              onClick={onEdit}
              aria-label="日記を直す"
              className="text-[var(--color-sub)]"
            >
              <Pencil className="size-3.5" aria-hidden />
            </button>
          )}
          {onDelete !== undefined && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="日記を削除"
              className="text-[var(--color-sub)]"
            >
              <Trash2 className="size-3.5" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {prompt !== undefined && entry.promptAnswer !== null && (
        <div className="relative mt-3 rounded-sm bg-[var(--color-bg)] p-3 pt-5">
          {/* 付箋を貼ったように、お題タグを箱の上端からはみ出させる(絶対配置であとの行間には影響しない) */}
          <span className="diary-tag absolute -top-2.5 left-2">
            {diaryCategoryLabelOf(prompt.category)}
          </span>
          <p className="text-[12px] font-medium text-[var(--color-sub)]">{prompt.body}</p>
          <p className="mt-1 text-[13px] whitespace-pre-line">{entry.promptAnswer}</p>
        </div>
      )}

      {entry.body !== null && (
        <p className="diary-ruled mt-3 text-[13px] leading-[28px] whitespace-pre-line">
          {entry.body}
        </p>
      )}

      {mediaIds.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {mediaIds.map((mediaId) => {
            const url = thumbUrlOf(mediaId);
            return (
              <button
                key={mediaId}
                type="button"
                onClick={() => {
                  onOpenMedia(mediaId);
                }}
                aria-label="画像を見る"
                className="size-16 overflow-hidden rounded-md bg-[var(--color-line)]"
              >
                {url !== undefined && (
                  <img src={url} alt="" loading="lazy" className="size-full object-cover" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
